import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { X, Building2, Upload, Trash2, Loader2, Check } from 'lucide-react';

const BillingProfileSetup = ({ onClose, onSaved }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getBillingProfile();
        setProfile(res.data);
      } catch {
        toast.error('Kunde inte hämta uppgifter');
      } finally { setLoading(false); }
    })();
  }, []);

  const setField = (k, v) => setProfile(p => ({ ...p, [k]: v }));

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Måste vara en bildfil'); return; }
    if (file.size > 500 * 1024) { toast.error('Max 500 KB. Komprimera bilden först.'); return; }
    setUploadingLogo(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.uploadBillingLogo(reader.result);
        setProfile(p => ({ ...p, logo_url: reader.result }));
        toast.success('Logga uppladdad');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Uppladdning misslyckades');
      } finally { setUploadingLogo(false); }
    };
    reader.readAsDataURL(file);
  };

  const handleLogoDelete = async () => {
    try {
      await api.deleteBillingLogo();
      setProfile(p => ({ ...p, logo_url: null }));
    } catch { toast.error('Kunde inte ta bort'); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // strippa metadata-fält som backend inte vill ha
      const payload = { ...profile };
      delete payload.company_id; delete payload._id; delete payload.created_at; delete payload.updated_at;
      delete payload.logo_url; // hanteras via separat endpoint
      await api.updateBillingProfile(payload);
      toast.success('Företagsuppgifter sparade');
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte spara');
    } finally { setSaving(false); }
  };

  if (loading || !profile) {
    return (
      <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center">
        <Loader2 className="text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-3 py-6" onClick={onClose} data-testid="billing-profile-modal">
      <div className="bg-card rounded-2xl shadow-2xl border border-border max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#1A1A1A] text-white px-6 py-4 flex items-center justify-between">
          <h2 className="text-base font-bold flex items-center gap-2"><Building2 size={18} className="text-[#E8613A]" /> Företagsuppgifter för fakturor</h2>
          <button onClick={onClose} className="hover:bg-white/10 rounded p-1"><X size={16} /></button>
        </div>
        <div className="p-6 overflow-y-auto space-y-5">
          {/* Logo */}
          <div>
            <Label className="text-xs">Företagslogga</Label>
            <p className="text-[11px] text-muted-foreground mb-2">Visas överst på alla fakturor. PNG/JPG, max 500 KB.</p>
            <div className="flex items-center gap-3">
              <div className="w-24 h-24 rounded-lg border-2 border-dashed border-border bg-muted/30 flex items-center justify-center overflow-hidden">
                {profile.logo_url ? (
                  <img src={profile.logo_url} alt="Företagslogga" className="max-w-full max-h-full object-contain" />
                ) : (
                  <Building2 size={28} className="text-muted-foreground/40" />
                )}
              </div>
              <div className="flex-1">
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" disabled={uploadingLogo} data-testid="logo-file-input" />
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#E8613A] hover:bg-[#D4532F] text-white text-xs font-medium cursor-pointer">
                    {uploadingLogo ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                    {profile.logo_url ? 'Byt logga' : 'Ladda upp logga'}
                  </span>
                </label>
                {profile.logo_url && (
                  <button onClick={handleLogoDelete} className="ml-2 text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1" data-testid="logo-delete-btn">
                    <Trash2 size={11} /> Ta bort
                  </button>
                )}
              </div>
            </div>
          </div>

          <Section title="Företagsuppgifter">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Företagsnamn *" value={profile.company_name} onChange={(v) => setField('company_name', v)} testid="bp-name" />
              <Field label="Org.nummer *" value={profile.org_number} onChange={(v) => setField('org_number', v)} testid="bp-org" placeholder="556677-8899" />
              <Field label="Momsreg.nr (VAT)" value={profile.vat_number} onChange={(v) => setField('vat_number', v)} testid="bp-vat" placeholder="SE556677889901" />
              <CheckboxField label="Innehar F-skattsedel" checked={profile.f_skatt} onChange={(v) => setField('f_skatt', v)} testid="bp-fskatt" />
            </div>
          </Section>

          <Section title="Adress">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Gatuadress *" value={profile.address} onChange={(v) => setField('address', v)} testid="bp-address" />
              <Field label="Postnr *" value={profile.postal_code} onChange={(v) => setField('postal_code', v)} testid="bp-postal" />
              <Field label="Ort *" value={profile.city} onChange={(v) => setField('city', v)} testid="bp-city" />
              <Field label="Land" value={profile.country} onChange={(v) => setField('country', v)} testid="bp-country" />
            </div>
          </Section>

          <Section title="Kontakt">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="E-post" value={profile.email} onChange={(v) => setField('email', v)} testid="bp-email" />
              <Field label="Telefon" value={profile.phone} onChange={(v) => setField('phone', v)} testid="bp-phone" />
              <Field label="Hemsida" value={profile.website} onChange={(v) => setField('website', v)} testid="bp-web" placeholder="www.dittforetag.se" />
            </div>
          </Section>

          <Section title="Betalningsuppgifter (minst en krävs)">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Bankgiro" value={profile.bankgiro} onChange={(v) => setField('bankgiro', v)} testid="bp-bg" placeholder="5050-1055" />
              <Field label="PlusGiro" value={profile.plusgiro} onChange={(v) => setField('plusgiro', v)} testid="bp-pg" />
              <Field label="IBAN" value={profile.iban} onChange={(v) => setField('iban', v)} testid="bp-iban" />
              <Field label="BIC/Swift" value={profile.bic} onChange={(v) => setField('bic', v)} testid="bp-bic" />
            </div>
          </Section>

          <Section title="Fakturaregler">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Faktura-prefix" value={profile.invoice_prefix} onChange={(v) => setField('invoice_prefix', v)} testid="bp-prefix" placeholder="F" />
              <Field label="Betalningsvillkor (dagar)" type="number" value={profile.default_payment_terms_days} onChange={(v) => setField('default_payment_terms_days', parseInt(v) || 30)} testid="bp-terms" />
              <Field label="Standard moms (%)" type="number" value={profile.default_vat_rate} onChange={(v) => setField('default_vat_rate', parseFloat(v) || 25)} testid="bp-vat-rate" />
            </div>
          </Section>
        </div>
        <div className="px-6 py-4 border-t border-border bg-muted/30 flex items-center justify-between">
          <button onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground">Avbryt</button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#E8613A] hover:bg-[#D4532F] text-white rounded-full h-10 px-6" data-testid="bp-save-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <><Check size={14} className="mr-1.5" /> Spara</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div>
    <h3 className="text-xs font-bold uppercase tracking-widest text-[#E8613A] mb-2">{title}</h3>
    {children}
  </div>
);

const Field = ({ label, value, onChange, type = 'text', placeholder, testid }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} className="mt-1" placeholder={placeholder} data-testid={testid} />
  </div>
);

const CheckboxField = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-2 mt-6 cursor-pointer">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="rounded" data-testid={testid} />
    <span className="text-sm">{label}</span>
  </label>
);

export default BillingProfileSetup;
